import type { Terrain } from '../game/Terrain';
import type { AntWorld } from './AntWorld';
import type { SpiderSim } from './Spider';

export interface LizardState {
  alive: boolean;
  x: number;
  y: number;
  z: number;
  heading: number;
  speed: number;
  /** Seconds until next heading nudge. */
  turnT: number;
  /** Day number at which we last vanished — used to decide if we're allowed back. */
  nextSpawnDay: number;
  /** Hit points. A horde of ~200 ants biting simultaneously can pull him down. */
  hp: number;
  maxHp: number;
  /** Seconds of panic-sprint remaining; refreshed while he's under attack. */
  panicT: number;
}

export interface LizardSimOptions {
  /** First day the lizard appears. */
  firstDay?: number;
  /** Days between visits when he leaves on schedule. */
  recurrenceDays?: number;
  /** Extra days off when he wanders off the edge. */
  banishmentDays?: number;
  /** World radius beyond which he's considered "off the map". */
  worldRadius?: number;
}

const RUN_SPEED = 2.8;              // lizard is fast
const CONTACT_BITE_R = 0.35;        // instant eat radius
const TURN_MIN = 2.5;
const TURN_MAX = 5.5;
const TURN_JITTER = 0.9;            // radians

const LIZARD_MAX_HP = 60;           // ~200 ants biting at 0.3 dps each ⇒ kill in ~1 s
const ANT_BITE_REACH = 0.25;        // ants within this range chew on him
const ANT_BITE_REACH_SQ = ANT_BITE_REACH * ANT_BITE_REACH;
const ANT_BITE_DPS = 0.3;           // per attacking ant
const PANIC_DURATION = 3.0;         // seconds of boosted speed after a bite lands
const PANIC_SPEED_MUL = 1.8;        // sprint-away boost while panicking

/**
 * One wandering lizard per world. First shows up on day 2, then every 2 days.
 * Enters from a random edge, sprints across the field; any ant or spider he
 * brushes past gets eaten. If he wanders off the map (outside `worldRadius`)
 * he vanishes for 3 days before the next visit.
 */
export class LizardSim {
  readonly state: LizardState;
  private readonly firstDay: number;
  private readonly recurrenceDays: number;
  private readonly banishmentDays: number;
  private readonly worldRadius: number;
  private initialised = false;

  constructor(
    private terrain: Terrain,
    private world: AntWorld,
    private spiders: SpiderSim,
    opts: LizardSimOptions = {},
  ) {
    this.firstDay = opts.firstDay ?? 2;
    this.recurrenceDays = opts.recurrenceDays ?? 2;
    this.banishmentDays = opts.banishmentDays ?? 3;
    this.worldRadius = opts.worldRadius ?? 38;
    this.state = {
      alive: false,
      x: 0, y: 0, z: 0,
      heading: 0, speed: 0,
      turnT: 0,
      nextSpawnDay: this.firstDay,
      hp: LIZARD_MAX_HP,
      maxHp: LIZARD_MAX_HP,
      panicT: 0,
    };
  }

  update(dt: number, currentDay: number): void {
    const s = this.state;
    if (!s.alive) {
      if (currentDay >= s.nextSpawnDay) this.spawn();
      return;
    }

    // Count ants currently biting the lizard and apply their combined DPS. If
    // any land a bite this frame, refresh his panic timer — a timer (rather
    // than per-frame boost) keeps him sprinting a beat AFTER breaking contact
    // so the escape reads as a flee rather than a frame-perfect twitch.
    let attackers = 0;
    for (let i = 0; i < this.world.ants.length; i++) {
      const a = this.world.ants[i];
      if (!a.alive || a.nestCycleT > 0) continue;
      const dx = a.x - s.x;
      const dz = a.z - s.z;
      if (dx * dx + dz * dz <= ANT_BITE_REACH_SQ) attackers++;
    }
    if (attackers > 0) {
      s.hp -= attackers * ANT_BITE_DPS * dt;
      s.panicT = PANIC_DURATION;
      if (s.hp <= 0) {
        s.alive = false;
        s.speed = 0;
        s.hp = 0;
        // Brief cooldown after a kill — the colony earned a break. Same
        // banishment window used when he wanders off the edge.
        s.nextSpawnDay = currentDay + this.banishmentDays;
        return;
      }
    }

    s.panicT = Math.max(0, s.panicT - dt);
    const speedMul = s.panicT > 0 ? PANIC_SPEED_MUL : 1;
    s.speed = RUN_SPEED * speedMul;

    // Occasional heading nudge toward nearest prey — otherwise random jitter.
    // When panicking he instead flees the densest ant cluster so a swarm can
    // actually chase him off the map.
    s.turnT -= dt;
    if (s.turnT <= 0) {
      if (s.panicT > 0) {
        const flee = this.swarmCentroid(s.x, s.z, 2.5);
        if (flee) {
          s.heading = Math.atan2(s.z - flee.z, s.x - flee.x) + (Math.random() - 0.5) * 0.4;
        } else {
          s.heading += (Math.random() - 0.5) * TURN_JITTER;
        }
        s.turnT = 0.4 + Math.random() * 0.6;
      } else {
        const prey = this.nearestPrey(s.x, s.z, 6);
        if (prey) {
          s.heading = Math.atan2(prey.z - s.z, prey.x - s.x) + (Math.random() - 0.5) * 0.3;
        } else {
          s.heading += (Math.random() - 0.5) * TURN_JITTER;
        }
        s.turnT = TURN_MIN + Math.random() * (TURN_MAX - TURN_MIN);
      }
    }
    s.x += Math.cos(s.heading) * s.speed * dt;
    s.z += Math.sin(s.heading) * s.speed * dt;
    s.y = this.terrain.getHeight(s.x, s.z);

    // Eat any alive prey in contact.
    this.world.killArea(s.x, s.z, CONTACT_BITE_R);
    this.spiders.killArea(s.x, s.z, CONTACT_BITE_R);

    // Off-the-map: gone for `banishmentDays`.
    if (Math.hypot(s.x, s.z) > this.worldRadius + 1.5) {
      s.alive = false;
      s.speed = 0;
      s.nextSpawnDay = currentDay + this.banishmentDays;
    }
  }

  private spawn(): void {
    // Enter from a random edge on the +bounds ring, headed roughly inward.
    const edgeAng = Math.random() * Math.PI * 2;
    const entryR = this.worldRadius - 0.5;
    const sx = Math.cos(edgeAng) * entryR;
    const sz = Math.sin(edgeAng) * entryR;
    const inward = Math.atan2(-sz, -sx) + (Math.random() - 0.5) * 0.8;
    const s = this.state;
    s.alive = true;
    s.x = sx;
    s.z = sz;
    s.y = this.terrain.getHeight(sx, sz);
    s.heading = inward;
    s.speed = RUN_SPEED;
    s.turnT = TURN_MIN + Math.random() * (TURN_MAX - TURN_MIN);
    s.hp = LIZARD_MAX_HP;
    s.panicT = 0;
    this.initialised = true;
  }

  /** Centre-of-mass of ants within `maxR` — the point he should sprint away
   *  from while panicking. Returns null if nobody's close enough to avoid. */
  private swarmCentroid(x: number, z: number, maxR: number): { x: number; z: number } | null {
    const maxR2 = maxR * maxR;
    let sumX = 0, sumZ = 0, n = 0;
    for (const a of this.world.ants) {
      if (!a.alive || a.nestCycleT > 0) continue;
      const dx = a.x - x, dz = a.z - z;
      if (dx * dx + dz * dz > maxR2) continue;
      sumX += a.x;
      sumZ += a.z;
      n++;
    }
    if (n === 0) return null;
    return { x: sumX / n, z: sumZ / n };
  }

  /** On scheduled departure (arrival day + recurrence), reset for the next visit. */
  markScheduledDeparture(currentDay: number): void {
    this.state.nextSpawnDay = currentDay + this.recurrenceDays;
  }

  private nearestPrey(x: number, z: number, maxR: number): { x: number; z: number } | null {
    const maxR2 = maxR * maxR;
    let bestD2 = maxR2;
    let best: { x: number; z: number } | null = null;
    for (const a of this.world.ants) {
      if (!a.alive) continue;
      const dx = a.x - x, dz = a.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = { x: a.x, z: a.z }; }
    }
    for (const sp of this.spiders.spiders) {
      if (!sp.alive) continue;
      const dx = sp.x - x, dz = sp.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = { x: sp.x, z: sp.z }; }
    }
    void this.initialised;
    return best;
  }
}
